import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { useLazySearchMoviesAndShowsQuery } from '../actions/tmdbApiSlice';

const SearchForm = ({ compact = false }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [triggerSearch, { data }] = useLazySearchMoviesAndShowsQuery();
  const anchorRef = useRef(null);

  useEffect(() => {
    if (data) {
      const filtered = data.results.filter(
        (r) => r.media_type === 'movie' || r.media_type === 'tv' || r.media_type === 'person',
      );
      setSuggestions(filtered.slice(0, 6));
      setShowSuggestions(true);
    }
  }, [data]);

  const debouncedSearch = useMemo(
    () =>
      debounce((q) => {
        if (q.length > 2) triggerSearch(q);
      }, 300),
    [triggerSearch],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    if (newQuery.length > 2) {
      debouncedSearch(newQuery);
    } else {
      debouncedSearch.cancel();
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length > 0) {
      setShowSuggestions(false);
      navigate(`/search/?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery('');
    let year = '';
    if (suggestion.media_type === 'movie' && suggestion.release_date) {
      year = suggestion.release_date.split('-')[0];
    } else if (suggestion.media_type === 'tv' && suggestion.first_air_date) {
      year = suggestion.first_air_date.split('-')[0];
    }
    const titleWithYear = year
      ? `${suggestion.title || suggestion.name} `
      : suggestion.title || suggestion.name;

    if (suggestion.media_type === 'person') {
      navigate(`/person/?q=${suggestion.name}`);
    } else {
      const idParam = suggestion.media_type === 'tv' ? 'tid' : 'mid';
      navigate(`/details/?q=${titleWithYear}&${idParam}=${suggestion.id}`);
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const clear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const labelFor = (mt) => (mt === 'tv' ? 'TV' : mt === 'movie' ? 'Movie' : 'Person');

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <Box sx={{ position: 'relative', maxWidth: compact ? 560 : 720, mx: 'auto', width: '100%' }}>
        <Paper
          ref={anchorRef}
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            border: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            transition: 'border-color .2s, box-shadow .2s',
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}22`,
            },
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mx: 0.5 }} />
          <InputBase
            value={query}
            onChange={handleInputChange}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Search movies, TV shows, people…"
            sx={{ flex: 1, ml: 1, fontSize: 14 }}
            inputProps={{ 'aria-label': 'search' }}
          />
          {query && (
            <IconButton size="small" onClick={clear} aria-label="clear">
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>

        <Popper
          open={showSuggestions && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
        >
          <Paper
            elevation={6}
            sx={{
              mt: 1,
              borderRadius: 3,
              overflow: 'hidden',
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <List dense disablePadding>
              {suggestions.map((s) => {
                const img = s.media_type === 'person' ? s.profile_path : s.poster_path;
                return (
                  <ListItemButton
                    key={`${s.media_type}-${s.id}`}
                    onClick={() => handleSuggestionClick(s)}
                    sx={{ gap: 1.5 }}
                  >
                    <Avatar
                      variant="rounded"
                      src={img ? `https://image.tmdb.org/t/p/w154${img}` : undefined}
                      alt={s.title || s.name}
                      sx={{ width: 40, height: 56, bgcolor: 'background.default' }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap variant="body2" fontWeight={600}>
                        {s.title || s.name}
                      </Typography>
                      <Typography noWrap variant="caption" color="text.secondary">
                        {s.release_date || s.first_air_date || ''}
                      </Typography>
                    </Box>
                    <Chip size="small" label={labelFor(s.media_type)} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchForm;
